import {TOKENS} from "../../../constants/tokens";
import {HexMath} from "../../HexMath";
import {ConstructionAccessibility} from "../../ConstructionAccessibility";
import {QUARTERS_OF_DIFFERENT_CITIES_MIN_HEXES_BETWEEN} from "../../../constants/construction";
import {
  selectHexes,
  selectPendingBuildings,
  selectPlayers,
  selectSpecialBuildings
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {calculateFutureCitiesCache} from "../calculateFutureCitiesCache";
import {
  calculateAvailableConstructionCellsForCommonBuilding
} from "./calculateAvailableConstructionCellsForCommonBuilding";
import {calculateBlockedConstructionCells} from "./calculateBlockedConstructionCells";
import {getNeutralPlayerMock} from "../../players/getNeutralPlayerMock";

function collectAllQuarterCells(citiesCache) {
  const quarterCells = [];

  if (!citiesCache) {
    return quarterCells;
  }

  Object.values(citiesCache).forEach((playerCities) => {
    playerCities.forEach((city) => {
      if (city.centralQuarter) {
        quarterCells.push(city.centralQuarter);
      }
      if (city[TOKENS.QUARTER] && Array.isArray(city[TOKENS.QUARTER])) {
        city[TOKENS.QUARTER].forEach((quarterKey) => {
          quarterCells.push(quarterKey);
        });
      }
    });
  });

  return quarterCells;
}

/**
 * Рассчитывает пустые ячейки и ячейки с дорогами, находящиеся на расстоянии 2 или меньше
 * от любого квартала (обычного или центрального), включая кварталы из pendingBuildings.
 *
 * @param state
 * @param [precomputedFutureCitiesCache] - уже посчитанный для этого же состояния
 *   calculateFutureCitiesCache, чтобы не считать его заново
 * @returns {Set<string>}
 */
export const calculateQuarterZoneCells = (state, precomputedFutureCitiesCache = null) => {
  const hexes = selectHexes(state);
  const pendingBuildings = selectPendingBuildings(state);
  const specialBuildings = selectSpecialBuildings(state);

  const futureCitiesCache = precomputedFutureCitiesCache
    || calculateFutureCitiesCache(hexes, specialBuildings, pendingBuildings);
  const quarterCells = collectAllQuarterCells(futureCitiesCache);

  if (quarterCells.length === 0) {
    return new Set();
  }

  const hexMath = new HexMath(30, 'flat');
  const constructionAccessibility = new ConstructionAccessibility(hexMath);
  const zoneCells = new Set();

  quarterCells.forEach((quarterKey) => {
    const reachableCells = constructionAccessibility.findReachableCells(
      quarterKey,
      QUARTERS_OF_DIFFERENT_CITIES_MIN_HEXES_BETWEEN,
      hexes,
    );

    reachableCells.forEach((cellKey) => {
      const token = hexes[cellKey];
      if (!token || token === TOKENS.ROAD) {
        zoneCells.add(cellKey);
      }
    });
  });

  return zoneCells;
};

/**
 * Рассчитывает ячейки, на которых любой игрок может построить обычный (базовый) квартал.
 * Используется для отображения специальной иконки поверх зоны влияния кварталов.
 *
 * Отряды противника намеренно не учитываются: оверлей должен показывать потенциальную
 * возможность строительства базового квартала независимо от того, что ячейка сейчас
 * временно заблокирована смежным вражеским отрядом (тот может быть уничтожен впоследствии).
 *
 * Наряду с реальными игроками, учитывается и нейтральный "игрок" (см. getNeutralPlayerMock):
 * без него ячейки, смежные с центральным кварталом нейтрального города, никогда не попадали бы
 * в исключение "смежно своему центральному кварталу" внутри calculateBlockedConstructionCellsForBaseQuarter,
 * так как нейтральный город не представлен ни одним элементом selectPlayers.
 *
 * @param state
 * @returns {Set<string>}
 */
export const calculateBaseQuarterAllowedCells = (state) => {
  const players = selectPlayers(state);
  if (!players || players.length === 0) {
    return new Set();
  }

  const hexes = selectHexes(state);
  const pendingBuildings = selectPendingBuildings(state);
  const specialBuildings = selectSpecialBuildings(state);
  const futureCitiesCache = calculateFutureCitiesCache(hexes, specialBuildings, pendingBuildings);

  const allowedCells = new Set();

  [...players, getNeutralPlayerMock()].forEach((player) => {
    const availableCells = calculateAvailableConstructionCellsForCommonBuilding(
      state, player, {ignoreEnemyUnitsBlocking: true}
    );
    const {blockedCells} = calculateBlockedConstructionCells(
      TOKENS.QUARTER,
      hexes,
      futureCitiesCache,
      player.color,
      availableCells
    );

    availableCells.forEach((cellKey) => {
      if (!blockedCells.has(cellKey)) {
        allowedCells.add(cellKey);
      }
    });
  });

  return allowedCells;
};
